import { Controller } from '@hotwired/stimulus'

const STATES = ['both', 'applied', 'unapplied']

// Quick search on the week show jobs table. Mirrors the customers index
// search bar: debounced term input, toggle chips, "Search in" / sort / area
// selects, date filters and async result swapping (no page reloads).
export default class extends Controller {
  static targets = ['form', 'term', 'reset', 'chip', 'results', 'clear', 'moreFiltersBadge']

  connect() {
    this.chipTargets.forEach((chip) => this.syncChip(chip))
    this.syncResetFlag()
    this.updateClearVisibility()
    this.updateMoreFiltersBadge()
  }

  // Filter chip click — cycle both → applied → unapplied, then search.
  cycle(event) {
    const chip = event.currentTarget
    const field = this.fieldFor(chip)
    if (!field) return

    const current = field.value || STATES[0]
    const next = STATES[(STATES.indexOf(current) + 1 + STATES.length) % STATES.length]
    field.value = next
    this.syncChip(chip)
    this.search()
  }

  // Search term typing — keep the reset flag in sync and debounce the search.
  termChanged() {
    this.syncResetFlag()
    this.resultsTarget.classList.add('is-loading')
    this.scheduleSearch()
  }

  // A select / date / checkbox changed — search immediately.
  changed() {
    if (this.clearing) return
    this.search()
  }

  // Per-page select lives in the results fragment; sync it into the form's
  // hidden per_page field before searching.
  perPageChanged(event) {
    const field = this.formTarget.querySelector('input[name="per_page"]')
    if (field) field.value = event.target.value
    this.search()
  }

  // Reset every filter to its default and search again (client-side clear).
  clearSearch(event) {
    if (event) event.preventDefault()
    this.clearing = true

    if (this.hasTermTarget) this.termTarget.value = ''
    this.syncResetFlag()

    const defaults = {
      quick_search_bookmarked: 'both',
      quick_search_assigned: 'both',
      quick_search_active: 'applied',
      quick_search_owing: 'both',
      quick_search_future_jobs: 'both'
    }
    Object.entries(defaults).forEach(([name, value]) => {
      const field = this.formTarget.querySelector(`input[name="${name}"]`)
      if (field) field.value = value
    })
    this.chipTargets.forEach((chip) => this.syncChip(chip))

    // Tom Select selects back to empty.
    this.formTarget.querySelectorAll('[data-controller~="select"]').forEach((el) => {
      const ctrl = this.application.getControllerForElementAndIdentifier(el, 'select')
      if (ctrl && ctrl.instance) ctrl.instance.clear()
    })

    // datepickers back to empty.
    this.formTarget.querySelectorAll('[data-controller~="datepicker"]').forEach((el) => {
      const ctrl = this.application.getControllerForElementAndIdentifier(el, 'datepicker')
      if (ctrl && ctrl.instance) ctrl.instance.clear()
    })

    this.clearing = false
    this.search()
  }

  // Native form submit (Enter in the field).
  submit(event) {
    if (event) event.preventDefault()
    this.search()
  }

  // Pagination links inside the results fragment.
  async paginate(event) {
    const link = event.target.closest('.ds-pagination a')
    if (!link) return

    event.preventDefault()
    const url = new URL(link.href, window.location.origin)
    url.searchParams.set('partial', '1')
    await this.load(url.toString())
    this.scrollToTable(this.resultsTarget)
  }

  scrollToTable(container) {
    const table = container.querySelector('.ds-table')
    const target = table || container
    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  scheduleSearch() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.search(), 250)
  }

  search() {
    clearTimeout(this.timer)
    this.load(this.buildUrl())
  }

  async load(url) {
    this.resultsTarget.classList.add('is-loading')
    try {
      const response = await fetch(url, { headers: { Accept: 'text/html' } })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const html = await response.text()
      this.resultsTarget.innerHTML = html
      this.updateClearVisibility()
      this.updateMoreFiltersBadge()
      this.syncUrl()
    } catch (error) {
      // Fall back to a full navigation so the user still gets a result/error.
      window.location.assign(url)
    } finally {
      this.resultsTarget.classList.remove('is-loading')
    }
  }

  buildUrl() {
    const base = this.formTarget.action.split('?')[0]
    const params = new URLSearchParams(new FormData(this.formTarget))
    params.set('partial', '1')
    return `${base}?${params.toString()}`
  }

  syncUrl() {
    const base = this.formTarget.action.split('?')[0]
    const params = new URLSearchParams(new FormData(this.formTarget))
    window.history.replaceState({}, '', `${base}?${params.toString()}`)
  }

  updateClearVisibility() {
    if (!this.hasClearTarget) return
    this.clearTarget.classList.toggle('hidden', this.isDefaultState())
  }

  isDefaultState() {
    const data = new FormData(this.formTarget)
    const val = (name) => (data.get(name) || '').trim()

    if (val('quick_search_term')) return false
    if (val('quick_search_sort')) return false
    if (val('quick_search_area_id')) return false
    if (val('last_clean_from')) return false
    if (val('last_clean_to')) return false
    if (val('last_contacted_before')) return false

    const defaults = {
      quick_search_bookmarked: 'both',
      quick_search_assigned: 'both',
      quick_search_active: 'applied',
      quick_search_owing: 'both',
      quick_search_future_jobs: 'both'
    }
    return Object.entries(defaults).every(([name, def]) => (data.get(name) || def) === def)
  }

  updateMoreFiltersBadge() {
    if (!this.hasMoreFiltersBadgeTarget) return
    this.moreFiltersBadgeTarget.classList.toggle('hidden', !this.hasMoreFilters())
  }

  hasMoreFilters() {
    const data = new FormData(this.formTarget)
    const val = (name) => (data.get(name) || '').trim()

    return !!(val('quick_search_sort') || val('quick_search_area_id') ||
      val('last_clean_from') || val('last_clean_to') || val('last_contacted_before'))
  }

  syncResetFlag() {
    if (!this.hasResetTarget) return

    const empty = !this.hasTermTarget || this.termTarget.value === ''
    this.resetTarget.value = empty ? 'true' : 'false'
  }

  fieldFor(chip) {
    return this.formTarget.querySelector(`input[name="${chip.dataset.field}"]`)
  }

  syncChip(chip) {
    const field = this.fieldFor(chip)
    const value = field ? field.value || STATES[0] : STATES[0]
    const index = Math.max(STATES.indexOf(value), 0)

    STATES.forEach((state) => chip.classList.remove(state))
    chip.classList.add(STATES[index])

    const label = chip.dataset[`tooltip-${index}`]
    if (label) {
      chip.setAttribute('title', label)
      chip.setAttribute('aria-label', label)
    }
    chip.setAttribute('aria-pressed', value === 'both' ? 'false' : 'true')
  }
}
